import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { DateRangePicker, type DateRange } from '@/components/common/DatePicker/DateRangePicker'
import { toast } from '@/components/common/Toast'
import { Icon } from '@/components/icons'
import { Button, Input, Label } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLocale, useTranslations } from '@/hooks/useTranslations'
import { cn } from '@/lib/utils'
import {
  useCreateTempWorkerAdvance,
  useSettleTempWorker,
  useTempWorkerPaymentSummary,
  type TempWorker,
} from '@/modules/temp-worker/services/tempWorker'
import { formatPrice } from '@/utils/number'

function parseYmd(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return undefined
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0)
}

function toYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function todayYmd(): string {
  return toYmd(new Date())
}

function startOfCurrentMonthYmd(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function formatWorkDate(ymd: string | null | undefined, locale: string) {
  if (!ymd) return '—'
  const d = parseYmd(ymd)
  if (!d) return ymd
  return d.toLocaleDateString(locale === 'th' ? 'th-TH' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

type Props = {
  worker: TempWorker
}

export function TempWorkerSettlementPanel({ worker }: Props) {
  const t = useTranslations('temp_worker_settlement')
  const locale = useLocale()
  const [startDate, setStartDate] = useState(() => startOfCurrentMonthYmd())
  const [endDate, setEndDate] = useState(() => todayYmd())
  const [advanceOpen, setAdvanceOpen] = useState(false)
  const [advanceAmount, setAdvanceAmount] = useState('')
  const [advanceNote, setAdvanceNote] = useState('')

  const summaryParams = useMemo(
    () => ({
      start_work_date: startDate,
      cutoff_work_date: endDate,
    }),
    [startDate, endDate],
  )

  const { data: paymentRes, isLoading, refetch } = useTempWorkerPaymentSummary(
    worker.id,
    summaryParams,
  )
  const $advance = useCreateTempWorkerAdvance()
  const $settle = useSettleTempWorker()

  const payment = paymentRes?.data
  const preview = payment?.preview
  const workerLive = payment?.worker ?? worker

  const settleDisabledReason = useMemo(() => {
    if (isLoading) return null
    if (!preview) return t('select_date_range')
    if (preview.unpaid_session_count === 0 || preview.gross_wage_baht <= 0) {
      return t('settle_disabled_no_wage')
    }
    if (preview.net_cash_paid_baht <= 0) {
      return t('settle_disabled_net_zero')
    }
    return null
  }, [preview, isLoading, t])

  const canSettle = Boolean(preview && settleDisabledReason === null)

  const dateRangeValue = useMemo((): DateRange | undefined => {
    if (!startDate && !endDate) return undefined
    return {
      from: startDate ? parseYmd(startDate) : undefined,
      to: endDate ? parseYmd(endDate) : undefined,
    }
  }, [startDate, endDate])

  const onDateRangeChange = (r: DateRange | undefined) => {
    if (!r?.from && !r?.to) {
      setStartDate(startOfCurrentMonthYmd())
      setEndDate(todayYmd())
      return
    }
    setStartDate(r.from ? toYmd(r.from) : startDate)
    setEndDate(r.to ? toYmd(r.to) : endDate)
  }

  const onAdvance = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = parseFloat(advanceAmount)
    if (Number.isNaN(n) || n <= 0) {
      toast.toastFailed(t('invalid_amount'))
      return
    }
    try {
      await $advance.mutateAsync({
        id: worker.id,
        amount_baht: n,
        note: advanceNote.trim() || undefined,
      })
      toast.toastSuccess(t('advance_ok'))
      setAdvanceOpen(false)
      setAdvanceAmount('')
      setAdvanceNote('')
      refetch()
    } catch (err: any) {
      toast.toastFailed(err?.response?.data?.message || err?.message || t('advance_fail'))
    }
  }

  const onSettle = async () => {
    if (!canSettle) {
      toast.toastFailed(settleDisabledReason ?? t('nothing_to_settle'))
      return
    }
    try {
      await $settle.mutateAsync({
        id: worker.id,
        cutoff_work_date: endDate,
        start_work_date: startDate,
      })
      toast.toastSuccess(t('settle_ok'))
      refetch()
    } catch (err: any) {
      toast.toastFailed(err?.response?.data?.message || err?.message || t('settle_fail'))
    }
  }

  return (
    <div className="rounded-xl border border-border/60 bg-card shadow-sm shadow-black/5">
      <div className="border-b border-border/60 px-6 py-4">
        <h2 className="text-lg font-semibold tracking-tight">{t('title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('subtitle')}</p>
        <p className="mt-3 rounded-lg border border-blue-200/60 bg-blue-50/60 px-3 py-2 text-xs leading-relaxed text-blue-950 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-100">
          {t('credit_explainer')}
        </p>
      </div>

      <div className="grid gap-6 p-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <StatBox
              label={t('paid_through')}
              value={formatWorkDate(workerLive.paid_through_work_date, locale)}
              accent="emerald"
            />
            <StatBox
              label={t('credit_balance')}
              hint={t('credit_balance_hint')}
              value={`${formatPrice(workerLive.credit_balance ?? 0)} ${t('baht')}`}
              accent="blue"
            />
          </div>
          <StatBox
            label={t('unpaid_total')}
            value={`${formatPrice(payment?.unpaid_gross_baht ?? 0)} ${t('baht')}`}
          />

          <div>
            <Label className="text-xs text-muted-foreground">{t('cutoff_date_range')}</Label>
            <DateRangePicker
              className="mt-1.5 h-10 w-full max-w-md justify-between"
              value={dateRangeValue}
              onChange={onDateRangeChange}
            />
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setAdvanceOpen(true)}>
                <Icon icon="mdi:cash-plus" className="mr-2 size-4" />
                {t('advance_btn')}
              </Button>
              <Button
                type="button"
                className="bg-slate-800 text-white hover:bg-slate-900"
                disabled={$settle.isLoading || !canSettle}
                onClick={onSettle}
              >
                {t('settle_btn')}
              </Button>
            </div>
            {settleDisabledReason ? (
              <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                {settleDisabledReason}
              </p>
            ) : null}
          </div>
        </div>

        <div className="rounded-lg border border-border/50 bg-muted/20 p-4">
          <p className="text-sm font-medium text-muted-foreground">{t('preview_title')}</p>
          {isLoading ? (
            <p className="mt-3 text-sm text-muted-foreground">{t('loading')}</p>
          ) : preview ? (
            <dl className="mt-3 space-y-2 text-sm">
              <PreviewRow label={t('gross_wage')} value={`${formatPrice(preview.gross_wage_baht)} ${t('baht')}`} />
              <PreviewRow
                label={t('credit_applied')}
                hint={t('credit_applied_hint')}
                value={`${formatPrice(preview.credit_applied_baht)} ${t('baht')}`}
              />
              <PreviewRow
                label={t('net_cash')}
                value={`${formatPrice(preview.net_cash_paid_baht)} ${t('baht')}`}
                bold
              />
              <PreviewRow
                label={t('remaining_credit')}
                hint={t('remaining_credit_hint')}
                value={`${formatPrice(preview.remaining_credit_baht)} ${t('baht')}`}
              />
              <PreviewRow label={t('working_days')} value={String(preview.working_days)} />
            </dl>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">{t('select_date_range')}</p>
          )}
        </div>
      </div>

      {(payment?.transactions?.length ?? 0) > 0 ? (
        <div className="border-t border-border/60 px-6 pb-6">
          <h3 className="mb-3 text-sm font-semibold">{t('txn_history')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">{t('txn_date')}</th>
                  <th className="py-2 pr-4 font-medium">{t('txn_type')}</th>
                  <th className="py-2 pr-4 text-right font-medium">{t('txn_amount')}</th>
                  <th className="py-2 font-medium">{t('txn_note')}</th>
                  <th className="py-2 w-12 font-medium" />
                </tr>
              </thead>
              <tbody>
                {payment!.transactions.map((txn) => (
                  <tr key={txn.id} className="border-b border-border/40 last:border-0">
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(txn.created_at).toLocaleString(locale === 'th' ? 'th-TH' : 'en-US', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>
                    <td className="py-2 pr-4">
                      {txn.txn_type === 'advance' ? t('type_advance') : t('type_settlement')}
                      {txn.cutoff_work_date ? (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({formatWorkDate(txn.cutoff_work_date, locale)})
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-right font-medium tabular-nums">
                      {formatPrice(txn.amount_baht)} {t('baht')}
                    </td>
                    <td className="py-2 text-muted-foreground">{txn.note ?? '—'}</td>
                    <td className="py-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title={t('slip_print')}
                        asChild
                      >
                        <Link
                          to="/admin/temp-workers/$id/slip/$txnId"
                          params={{ id: worker.id, txnId: txn.id }}
                        >
                          <Icon icon="mdi:printer" className="size-4" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('advance_dialog_title')}</DialogTitle>
            <p className="text-sm text-muted-foreground">{t('advance_dialog_desc')}</p>
          </DialogHeader>
          <form onSubmit={onAdvance} className="space-y-4">
            <div>
              <Label htmlFor="adv-amount">{t('advance_amount')}</Label>
              <Input
                id="adv-amount"
                type="number"
                min={0.01}
                step="0.01"
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="adv-note">{t('advance_note')}</Label>
              <Input
                id="adv-note"
                value={advanceNote}
                onChange={(e) => setAdvanceNote(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdvanceOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={$advance.isLoading}>
                {t('confirm_advance')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function StatBox({
  label,
  value,
  hint,
  accent,
}: {
  label: string
  value: string
  hint?: string
  accent?: 'emerald' | 'blue'
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border/50 p-3',
        accent === 'emerald' && 'border-emerald-200/60 bg-emerald-50/50 dark:bg-emerald-950/20',
        accent === 'blue' && 'border-blue-200/60 bg-blue-50/50 dark:bg-blue-950/20',
      )}
    >
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {hint ? <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground/90">{hint}</p> : null}
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}

function PreviewRow({
  label,
  value,
  hint,
  bold,
}: {
  label: string
  value: string
  hint?: string
  bold?: boolean
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className={cn('tabular-nums', bold && 'font-bold text-blue-600 dark:text-blue-400')}>
          {value}
        </dd>
      </div>
      {hint ? <p className="text-[11px] leading-snug text-muted-foreground/90">{hint}</p> : null}
    </div>
  )
}
